import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { 
  Bot, X, Send, BrainCircuit, Sparkles, 
  ChevronRight, Calendar, Users, DollarSign, Activity 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../context/NotificationContext';
import './AICopilot.css';

const AICopilot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([
    { role: 'assistant', content: '¡Hola! Soy GDT AI, tu asistente inteligente. ¿En qué puedo ayudarte hoy con la gestión de la clínica?' }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [contextData, setContextData] = useState({
    appointments: [],
    patients: [],
    transactions: [],
    stats: {}
  });
  const [patientContext, setPatientContext] = useState(null);
  const [proactiveAlert, setProactiveAlert] = useState(null); // { id, title, message, type, actionText, onAction, autoDismiss }
  const [isMuted, setIsMuted] = useState(localStorage.getItem('gdt_ai_muted') === 'true');
  const alertedTurnIds = useRef(new Set());
  const autoDismissTimer = useRef(null);
  
  const { addNotification } = useNotifications();
  const chatEndRef = useRef(null);

  const toggleMute = () => {
    const nextState = !isMuted;
    setIsMuted(nextState);
    localStorage.setItem('gdt_ai_muted', String(nextState));
    if (nextState) setProactiveAlert(null);
  };
  
  // Try to get API key from localStorage or default
  const [dbApiKey, setDbApiKey] = useState(localStorage.getItem('gdt_ai_key') || '');
  const [dbModel, setDbModel] = useState(localStorage.getItem('gdt_ai_model') || 'gemini-1.5-flash');

  const apiKey = dbApiKey || localStorage.getItem('gdt_ai_key') || '';

  useEffect(() => {
    const fetchGlobalAiSettings = async () => {
      try {
        const { data } = await supabase
          .from('system_settings')
          .select('key, value')
          .in('key', ['gdt_ai_key', 'gdt_ai_model']);

        if (data && data.length > 0) {
          const keyRow = data.find(r => r.key === 'gdt_ai_key');
          const modelRow = data.find(r => r.key === 'gdt_ai_model');
          if (keyRow?.value) {
            setDbApiKey(keyRow.value);
            localStorage.setItem('gdt_ai_key', keyRow.value);
          }
          if (modelRow?.value) {
            setDbModel(modelRow.value);
            localStorage.setItem('gdt_ai_model', modelRow.value);
          }
        }
      } catch (err) {
        console.warn('[AICopilot] Could not fetch global AI settings:', err);
      }
    };

    fetchGlobalAiSettings();
  }, []);

  // AUTO DISMISS TIMER HELPER
  const triggerAlertWithAutoDismiss = (alertObj, durationMs = 8000) => {
    if (isMuted) return;
    if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
    
    setProactiveAlert(alertObj);
    autoDismissTimer.current = setTimeout(() => {
      setProactiveAlert(prev => prev?.id === alertObj.id ? null : prev);
    }, durationMs);
  };

  // REALTIME MONITOR & ROTATING SMART TIPS WORKER (Every 35 seconds)
  useEffect(() => {
    // 1. Supabase Realtime channel for new appointments
    const channel = supabase
      .channel('ai_realtime_appointments')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'appointments' },
        (payload) => {
          const newApp = payload.new;
          const patientName = newApp.patient_name || 'Nuevo Paciente';
          const appTime = newApp.appointment_time || '';
          
          addNotification({
            title: '🤖 GDT AI: Nuevo Turno Registrado',
            message: `${patientName} agendó para las ${appTime}.`,
            type: 'info'
          });

          triggerAlertWithAutoDismiss({
            id: `realtime-${newApp.id}`,
            title: '🆕 Nuevo Turno Registrado',
            message: `${patientName} tiene turno agendado para las ${appTime}. ¿Revisamos detalles con la IA?`,
            type: 'info',
            actionText: '💬 Abrir Chat IA',
            onAction: () => {
              setIsOpen(true);
              setInput(`Cuéntame los detalles del turno de ${patientName} agendado para hoy.`);
              setProactiveAlert(null);
            }
          }, 10000);
        }
      )
      .subscribe();

    // 2. Interval worker for upcoming appointments & periodic interactive tips
    let tipIndex = 0;
    const checkUpcomingInterval = setInterval(async () => {
      try {
        if (isMuted || isOpen) return; // Silent if muted or chat open

        const todayStr = new Date().toISOString().split('T')[0];
        const { data: upcomingApps } = await supabase
          .from('appointments')
          .select('*')
          .eq('appointment_date', todayStr)
          .in('status', ['pendiente', 'confirmado', 'en_espera']);

        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        let urgentAlertFound = false;

        if (upcomingApps && upcomingApps.length > 0) {
          for (const app of upcomingApps) {
            if (!app.appointment_time || alertedTurnIds.current.has(app.id)) continue;

            const [h, m] = app.appointment_time.split(':').map(Number);
            if (isNaN(h) || isNaN(m)) continue;
            
            const appMinutes = h * 60 + m;
            const diffMinutes = appMinutes - currentMinutes;

            // Alert if appointment is between 1 and 12 minutes away
            if (diffMinutes >= 1 && diffMinutes <= 12) {
              alertedTurnIds.current.add(app.id);
              urgentAlertFound = true;

              const alertTitle = `⏰ Turno Próximo en ${diffMinutes} min`;
              const alertMsg = `${app.patient_name} tiene turno a las ${app.appointment_time} hs.`;

              addNotification({
                title: alertTitle,
                message: alertMsg,
                type: 'warning'
              });

              triggerAlertWithAutoDismiss({
                id: `upcoming-${app.id}`,
                title: alertTitle,
                message: `${alertMsg} ¿Deseas enviarlo a la Sala de Espera?`,
                type: 'warning',
                actionText: '🛎️ Pasar a Sala de Espera',
                onAction: async () => {
                  await supabase.from('appointments').update({ status: 'en_espera' }).eq('id', app.id);
                  addNotification({ title: 'Estado Actualizado', message: `${app.patient_name} está en Sala de Espera.`, type: 'success' });
                  setProactiveAlert(null);
                }
              }, 12000);
              break;
            }
          }
        }

        // If no urgent appointment alert, display a friendly rotating interactive tip every 35 seconds
        if (!urgentAlertFound) {
          const totalApps = upcomingApps?.length || 0;
          const tipsPool = [
            {
              id: `tip-${Date.now()}-1`,
              title: '💡 Tip Operativo GDT',
              message: `Tienes ${totalApps} turnos agendados para hoy. ¿Quieres ver el resumen de la jornada?`,
              type: 'info',
              actionText: '📊 Ver Resumen',
              onAction: () => {
                setIsOpen(true);
                setInput('¿Cuál es el resumen operativo de hoy?');
                setProactiveAlert(null);
              }
            },
            {
              id: `tip-${Date.now()}-2`,
              title: '💰 Control de Caja',
              message: 'Recuerda verificar si hay coseguros o cobros pendientes antes del cierre.',
              type: 'info',
              actionText: '💵 Consultar Balance',
              onAction: () => {
                setIsOpen(true);
                setInput('¿Cuál es el balance actual de la caja?');
                setProactiveAlert(null);
              }
            },
            {
              id: `tip-${Date.now()}-3`,
              title: '⚡ Asistente por Voz/Chat',
              message: 'Puedes pedirme "Cancelar turno de [Paciente]" o "Agregar evolución médica" directamente.',
              type: 'info',
              actionText: '💬 Probar Comandos',
              onAction: () => {
                setIsOpen(true);
                setProactiveAlert(null);
              }
            }
          ];

          const currentTip = tipsPool[tipIndex % tipsPool.length];
          tipIndex++;

          triggerAlertWithAutoDismiss(currentTip, 7000); // Disappears gracefully in 7s
        }
      } catch (err) {
        console.warn('[AICopilot] Error in rotating monitor loop:', err);
      }
    }, 35000); // Runs every 35 seconds

    return () => {
      supabase.removeChannel(channel);
      clearInterval(checkUpcomingInterval);
      if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
    };
  }, [addNotification, isMuted, isOpen]);

  function scrollToBottom() {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }

  async function fetchContextData() {
    try {
      const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
      const staffName = activeStaff.full_name || '';

      // Fetch core data for AI context
      let appsQuery = supabase.from('appointments').select('*').limit(50);
      let patsQuery = supabase.from('patients').select('*').limit(20);
      let txsQuery = supabase.from('transactions').select('*').limit(20);

      if (staffName) {
        appsQuery = appsQuery.eq('professional_name', staffName);
        patsQuery = patsQuery.eq('professional_name', staffName);
        txsQuery = txsQuery.eq('professional_name', staffName);
      }

      const { data: apps } = await appsQuery;
      const { data: pats } = await patsQuery;
      const { data: txs } = await txsQuery;
      
      setContextData({
        appointments: apps || [],
        patients: pats || [],
        transactions: txs || [],
        stats: {
          totalApps: apps?.length || 0,
          totalPatients: pats?.length || 0,
          balance: txs?.reduce((acc, t) => acc + (t.type === 'ingreso' ? Number(t.amount) : -Number(t.amount)), 0) || 0
        }
      });
    } catch (err) {
      console.error("Error fetching AI context:", err);
    }
  }

  useEffect(() => {
    if (isOpen) {
      Promise.resolve().then(() => {
        fetchContextData();
      });
      scrollToBottom();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const handlePatientContext = (e) => {
      setPatientContext(e.detail);
    };
    window.addEventListener('ai_patient_context', handlePatientContext);
    return () => window.removeEventListener('ai_patient_context', handlePatientContext);
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    // Re-read API key and saved configuration
    let currentApiKey = localStorage.getItem('gdt_ai_key') || dbApiKey || '';
    let savedModel = localStorage.getItem('gdt_ai_model') || dbModel || 'gemini-1.5-flash';

    // ALWAYS check system_settings if currentApiKey is empty or not in localStorage
    if (!currentApiKey) {
      try {
        const { data } = await supabase
          .from('system_settings')
          .select('key, value')
          .in('key', ['gdt_ai_key', 'gdt_ai_model']);

        if (data && data.length > 0) {
          const keyRow = data.find(r => r.key === 'gdt_ai_key');
          const modelRow = data.find(r => r.key === 'gdt_ai_model');
          if (keyRow?.value) {
            currentApiKey = keyRow.value;
            localStorage.setItem('gdt_ai_key', keyRow.value);
            setDbApiKey(keyRow.value);
          }
          if (modelRow?.value) {
            savedModel = modelRow.value;
            localStorage.setItem('gdt_ai_model', modelRow.value);
            setDbModel(modelRow.value);
          }
        }
      } catch (err) {
        console.warn('Query for global AI key failed:', err);
      }
    }

    if (!currentApiKey) {
      setMessages(prev => [...prev, { role: 'user', content: input }]);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ No he detectado una API Key de Gemini configurada globalmente. Por favor, ve a Configuración > NEON AI Copilot e ingresa tu API Key.' 
      }]);
      setInput('');
      return;
    }
    
    if (currentApiKey === 'demo') {
      const userMessage = input;
      setInput('');
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      setIsLoading(true);
      
      setTimeout(() => {
        const demoResponses = [
          "¡Hola! En modo demo puedo confirmarte que el sistema está operando normalmente. Ingresa una API Key real para consultar la base de datos.",
          "Modo DEMO: Para consultar turnos específicos o pacientes en tiempo real con Gemini AI, introduce tu API Key en la pantalla de Configuración.",
          "He procesado tu consulta. En Configuración > NEON AI Copilot puedes ingresar tu clave gratuita de Google AI Studio."
        ];
        const randomResp = demoResponses[Math.floor(Math.random() * demoResponses.length)];
        setMessages(prev => [...prev, { role: 'assistant', content: `[MODO DEMO] ${randomResp}` }]);
        setIsLoading(false);
      }, 800);
      return;
    }

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const genAI = new GoogleGenerativeAI(currentApiKey.trim());
      
      const patientContextStr = patientContext ? `\nCONTEXTO DE PACIENTE ACTUAL EN PANTALLA:
        - Nombre: ${patientContext.patient.full_name}
        - DNI: ${patientContext.patient.dni}
        - Evoluciones clínicas registradas: ${JSON.stringify(patientContext.evolutions.map(e => ({ fecha: e.created_at, nota: e.content, profesional: e.professional_name })))}` : '';

      const systemPrompt = `Eres GDT AI, el asistente experto de la plataforma GDT (Gestión de Turnos).
        Tu tono es profesional, analítico y servicial. Siempre saluda como GDT AI.
        
        DATOS ACTUALES DE LA CLÍNICA:
        - Fecha de hoy: ${new Date().toLocaleDateString()}
        - Turnos recientes: ${JSON.stringify(contextData.appointments.slice(0, 10).map(a => ({ p: a.patient_name, d: a.appointment_date, t: a.appointment_time, s: a.status })))}
        - Saldo actual: $${contextData.stats.balance}
        - Total pacientes: ${contextData.stats.totalPatients}
        ${patientContextStr}
        
        CAPACIDADES Y ACCIONES DIRECTAS:
        1. Analizar tendencias y resumir el estado de la clínica o de un paciente en pantalla.
        2. Agendar un turno: Di "Procesando agendamiento..." y agrega:
        [ACTION:BOOK_TURN]{ "patient_name": "Nombre", "study_type": "Estudio", "date": "YYYY-MM-DD", "time": "HH:MM" }[/ACTION]
        3. Cancelar un turno: Di "Procesando cancelación..." y agrega:
        [ACTION:CANCEL_TURN]{ "patient_name": "Nombre del paciente" }[/ACTION]
        4. Cambiar estado de turno (ej: 'en_espera', 'completado', 'atendido', 'cancelado'): Di "Actualizando turno..." y agrega:
        [ACTION:UPDATE_STATUS]{ "patient_name": "Nombre", "status": "en_espera|completado|cancelado" }[/ACTION]
        5. Registrar evolución médica / nota de consulta para el paciente actual o por nombre: Di "Registrando evolución..." y agrega:
        [ACTION:ADD_EVOLUTION]{ "patient_name": "Nombre", "content": "Nota o evolución médica" }[/ACTION]
        6. Registrar un movimiento de caja / transacción: Di "Registrando transacción..." y agrega:
        [ACTION:REGISTER_TRANSACTION]{ "type": "ingreso|egreso", "amount": 5000, "description": "Detalle del pago o gasto" }[/ACTION]`;

      // Helper for robust discovery
      const attemptChat = async (mName) => {
        console.log(`[GDT AI] Intentando: ${mName}...`);
        const model = genAI.getGenerativeModel({ model: mName });
        
        // Build history from messages state
        const history = [
          { role: "user", parts: [{ text: systemPrompt }] },
          { role: "model", parts: [{ text: "Entendido. Soy GDT AI, el Copilot operativo de la clínica. Estoy listo para ejecutar acciones directas." }] }
        ];

        // Add last 4 messages for context (excluding the system one)
        messages.slice(-4).forEach(msg => {
          history.push({
            role: msg.role === 'user' ? 'user' : 'model',
            parts: [{ text: msg.content }]
          });
        });

        const chat = model.startChat({ history });
        const result = await chat.sendMessage(userMessage);
        const response = await result.response;
        return { text: response.text(), model: mName, version: 'v1' };
      };

      let successData = null;
      const modelCandidates = [
        savedModel, // Try saved one first
        "gemini-2.0-flash", 
        "gemini-flash-latest", 
        "gemini-1.5-flash",
        "gemini-pro-latest",
        "gemini-1.5-pro"
      ];

      // Multi-layer fallback loop
      for (const mName of modelCandidates) {
        if (!mName) continue;
        try {
          successData = await attemptChat(mName);
          if (successData) break;
        } catch (err) {
          console.warn(`[GDT AI] Falló ${mName}:`, err.message);
          // If it's a quota error, don't keep trying models, just wait
          if (err.message?.includes('quota')) throw err;
        }
      }

      if (!successData) {
        throw new Error("No se pudo establecer conexión con ningún modelo de Gemini. Verifica tu API Key.");
      }

      // Save working config
      localStorage.setItem('gdt_ai_model', successData.model);
      localStorage.setItem('gdt_ai_version', successData.version);

      const text = successData.text;

      // Check for actions
      const bookMatch = text.match(/\[ACTION:BOOK_TURN\](.*?)\[\/ACTION\]/s);
      const cancelMatch = text.match(/\[ACTION:CANCEL_TURN\](.*?)\[\/ACTION\]/s);
      const statusMatch = text.match(/\[ACTION:UPDATE_STATUS\](.*?)\[\/ACTION\]/s);
      const evoMatch = text.match(/\[ACTION:ADD_EVOLUTION\](.*?)\[\/ACTION\]/s);
      const txMatch = text.match(/\[ACTION:REGISTER_TRANSACTION\](.*?)\[\/ACTION\]/s);

      if (bookMatch) {
        try { await executeBookTurn(JSON.parse(bookMatch[1])); } catch (e) { console.error("Failed book turn action", e); }
      }
      if (cancelMatch) {
        try { await executeCancelTurn(JSON.parse(cancelMatch[1])); } catch (e) { console.error("Failed cancel turn action", e); }
      }
      if (statusMatch) {
        try { await executeUpdateStatus(JSON.parse(statusMatch[1])); } catch (e) { console.error("Failed update status action", e); }
      }
      if (evoMatch) {
        try { await executeAddEvolution(JSON.parse(evoMatch[1])); } catch (e) { console.error("Failed add evolution action", e); }
      }
      if (txMatch) {
        try { await executeRegisterTransaction(JSON.parse(txMatch[1])); } catch (e) { console.error("Failed transaction action", e); }
      }

      setMessages(prev => [...prev, { role: 'assistant', content: text.replace(/\[ACTION:.*?\]/gs, '').trim() }]);
    } catch (err) {
      console.error("Detailed AI Error:", err);
      let errorMsg = 'Lo siento, ha ocurrido un error al procesar tu solicitud.';
      
      if (err.message?.includes('API key not valid') || err.message?.includes('API_KEY_INVALID')) {
        errorMsg = '❌ La API Key no es válida. Por favor, revísala en Configuración > IA.';
      } else if (err.message?.includes('quota')) {
        errorMsg = '⏳ Se ha alcanzado el límite de cuota gratuita. Intenta de nuevo en un momento.';
      } else {
        errorMsg = `⚠️ Error: ${err.message || 'Revisa tu conexión y API Key.'}`;
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
    } finally {
      setIsLoading(false);
    }
  };

  const executeBookTurn = async (data) => {
    try {
      const { error } = await supabase.from('appointments').insert([{
        patient_name: data.patient_name,
        patient_email: data.patient_email || '',
        patient_phone: data.patient_phone || '',
        study_type: data.study_type,
        appointment_date: data.date,
        appointment_time: data.time,
        status: 'pendiente'
      }]);

      if (error) throw error;

      addNotification({
        title: 'Turno Agendado por IA',
        message: `${data.patient_name} agendado para ${data.study_type} el ${data.date}`,
        type: 'success'
      });
    } catch (err) {
      console.error("Error executing AI action:", err);
      addNotification({
        title: 'Error en Agendamiento IA',
        message: 'No pude concretar la reserva automáticamente.',
        type: 'error'
      });
    }
  };

  const executeCancelTurn = async (data) => {
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const { error } = await supabase.from('appointments')
        .update({ status: 'cancelado' })
        .ilike('patient_name', `%${data.patient_name}%`)
        .gte('appointment_date', todayStr);

      if (error) throw error;

      addNotification({
        title: 'Turno Cancelado por IA',
        message: `Se canceló el turno de ${data.patient_name}.`,
        type: 'warning'
      });
    } catch (err) {
      console.error("Error canceling turn:", err);
      addNotification({ title: 'Error IA', message: `No pude cancelar el turno de ${data.patient_name}.`, type: 'error' });
    }
  };

  const executeUpdateStatus = async (data) => {
    try {
      const todayStr = new Date().toISOString().split('T')[0];
      const { error } = await supabase.from('appointments')
        .update({ status: data.status })
        .ilike('patient_name', `%${data.patient_name}%`)
        .eq('appointment_date', todayStr);

      if (error) throw error;

      addNotification({
        title: 'Estado de Turno Actualizado',
        message: `${data.patient_name} marcado como '${data.status}'.`,
        type: 'info'
      });
    } catch (err) {
      console.error("Error updating status:", err);
      addNotification({ title: 'Error IA', message: `No pude cambiar el estado del turno.`, type: 'error' });
    }
  };

  const executeAddEvolution = async (data) => {
    try {
      const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
      const staffName = activeStaff.full_name || 'GDT AI Copilot';

      // Find patient ID by name if available
      let patientId = patientContext?.patient?.id;
      if (!patientId) {
        const { data: pData } = await supabase.from('patients')
          .select('id')
          .ilike('full_name', `%${data.patient_name}%`)
          .limit(1);
        if (pData && pData.length > 0) patientId = pData[0].id;
      }

      if (!patientId) {
        addNotification({ title: 'Error IA', message: `No encontré la ficha del paciente ${data.patient_name}.`, type: 'error' });
        return;
      }

      const { error } = await supabase.from('medical_evolutions').insert([{
        patient_id: patientId,
        professional_name: staffName,
        content: data.content,
        created_at: new Date().toISOString()
      }]);

      if (error) throw error;

      addNotification({
        title: 'Evolución Registrada por IA',
        message: `Nota guardada en la historia clínica de ${data.patient_name}.`,
        type: 'success'
      });
    } catch (err) {
      console.error("Error adding evolution:", err);
      addNotification({ title: 'Error IA', message: `No pude registrar la evolución médica.`, type: 'error' });
    }
  };

  const executeRegisterTransaction = async (data) => {
    try {
      const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
      const staffName = activeStaff.full_name || 'GDT AI Copilot';

      const { error } = await supabase.from('transactions').insert([{
        type: data.type || 'ingreso',
        amount: Number(data.amount) || 0,
        description: data.description || 'Movimiento registrado por GDT AI',
        professional_name: staffName,
        date: new Date().toISOString().split('T')[0]
      }]);

      if (error) throw error;

      addNotification({
        title: 'Caja Actualizada por IA',
        message: `${data.type === 'ingreso' ? 'Ingreso' : 'Egreso'} de $${data.amount} registrado.`,
        type: 'success'
      });
    } catch (err) {
      console.error("Error registering transaction:", err);
      addNotification({ title: 'Error IA', message: `No pude registrar el movimiento de caja.`, type: 'error' });
    }
  };

  return (
    <div className="ai-copilot-container">
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="ai-chat-window"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
          >
            <div className="ai-chat-header">
              <div className="ai-brand">
                <div className="ai-icon-bg">
                  <BrainCircuit size={20} />
                </div>
                <div>
                  <h4>GDT AI</h4>
                  <span className="online-indicator">● Activo</span>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} className="close-chat">
                <X size={20} />
              </button>
            </div>

            <div className="ai-messages-container">
              {messages.map((msg, i) => (
                <div key={i} className={`message ${msg.role}`}>
                  <div className="message-content">
                    {msg.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="message assistant loading">
                  <div className="dot-typing">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Quick Action Suggestions */}
            <div className="ai-quick-suggestions">
              <button 
                type="button" 
                className="ai-suggestion-chip"
                onClick={() => {
                  setInput('¿Cuál es el resumen operativo de hoy y los turnos de la jornada?');
                }}
              >
                📊 Resumen del día
              </button>
              <button 
                type="button" 
                className="ai-suggestion-chip"
                onClick={() => {
                  setInput('¿Cuál es el balance actual de caja y transacciones?');
                }}
              >
                💰 Balance de caja
              </button>
              {patientContext && (
                <button 
                  type="button" 
                  className="ai-suggestion-chip highlight"
                  onClick={() => {
                    setInput(`Por favor, hazme un resumen clínico completo del paciente ${patientContext.patient.full_name}.`);
                  }}
                >
                  🩺 Resumir paciente actual
                </button>
              )}
              <button 
                type="button" 
                className="ai-suggestion-chip"
                onClick={() => {
                  setInput('Quiero agendar un turno para ');
                }}
              >
                📅 Agendar turno
              </button>
            </div>

            <form className="ai-chat-input" onSubmit={handleSendMessage}>
              <input 
                type="text" 
                placeholder="Pregúntame algo..." 
                value={input}
                onChange={(e) => setInput(e.target.value)}
                autoFocus
              />
              <button type="submit" disabled={isLoading || !input.trim()}>
                <Send size={18} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Proactive Real-Time Banner */}
      <AnimatePresence>
        {proactiveAlert && !isOpen && (
          <motion.div 
            className={`ai-proactive-banner ${proactiveAlert.type}`}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
          >
            <div className="ai-proactive-header">
              <span className="ai-proactive-title">{proactiveAlert.title}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <button 
                  className="ai-proactive-close"
                  title={isMuted ? "Activar avisos" : "Silenciar avisos rotativos"}
                  onClick={toggleMute}
                >
                  {isMuted ? '🔕' : '🔔'}
                </button>
                <button 
                  className="ai-proactive-close"
                  onClick={() => setProactiveAlert(null)}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            <p className="ai-proactive-message">{proactiveAlert.message}</p>
            {proactiveAlert.actionText && (
              <div className="ai-proactive-actions">
                <button 
                  className="ai-proactive-btn primary"
                  onClick={proactiveAlert.onAction}
                >
                  {proactiveAlert.actionText}
                </button>
                <button 
                  className="ai-proactive-btn secondary"
                  onClick={() => setProactiveAlert(null)}
                >
                  Ignorar
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <button 
        className={`ai-fab ${isOpen ? 'open' : ''} ${proactiveAlert ? 'has-alert' : ''}`} 
        onClick={() => {
          setIsOpen(!isOpen);
          if (proactiveAlert) setProactiveAlert(null);
        }}
      >
        {isOpen ? <X size={24} /> : <Bot size={28} />}
        {!isOpen && <div className="ai-pulse"></div>}
      </button>
    </div>
  );
};

export default AICopilot;
